// Le rappel du tableau de bord : ce qui est en retard, et ce qui est pour
// aujourd'hui.
//
// Une liste de tâches qu'il faut penser à ouvrir ne sert à personne. Celle-ci
// vient à vous, sur le premier écran, et se coche sur place — le geste le plus
// fréquent ne doit pas demander de changer de page.
//
// La carte ne s'affiche que s'il y a quelque chose : un « rien à faire » de
// plus sur un tableau de bord déjà chargé n'apprend rien. Et si la lecture
// échoue, elle disparaît sans bruit plutôt que de casser l'accueil.

import { LIBELLE_PRIORITE, type Tache, tachesDuJour, trancheDe } from '@imp/shared/taches';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, CheckSquare } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { changerStatut, listTaches } from '@/modules/taches/api';

const A_MONTRER = 5;

export function CarteTachesDuJour() {
  const qc = useQueryClient();
  const taches = useQuery({ queryKey: ['taches'], queryFn: listTaches });

  const cocher = useMutation({
    mutationFn: (id: string) => changerStatut(id, 'fait'),
    onMutate: async (id: string) => {
      await qc.cancelQueries({ queryKey: ['taches'] });
      const avant = qc.getQueryData<Tache[]>(['taches']);
      qc.setQueryData<Tache[]>(['taches'], (liste) =>
        (liste ?? []).map((t) =>
          t.id === id ? { ...t, statut: 'fait' as const, completeAt: new Date().toISOString() } : t,
        ),
      );
      return { avant };
    },
    onError: (_erreur, _id, contexte) => {
      if (contexte?.avant !== undefined) qc.setQueryData(['taches'], contexte.avant);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['taches'] }),
  });

  if (taches.isError || taches.data === undefined) return null;

  const pressantes = tachesDuJour(taches.data, new Date(), A_MONTRER);
  if (pressantes.length === 0) return null;

  const enRetard = pressantes.filter((t) => trancheDe(t) === 'en_retard').length;
  const total = tachesDuJour(taches.data, new Date(), Number.MAX_SAFE_INTEGER).length;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <CheckSquare className="h-5 w-5 shrink-0" />Mes tâches du jour
          {enRetard > 0 ? (
            <span className="text-prio-high text-sm font-normal">— {enRetard} en retard</span>
          ) : null}
        </CardTitle>
        <Button asChild variant="outline" size="sm">
          <Link to="/taches">
            Tout voir <ArrowRight className="h-5 w-5" />
          </Link>
        </Button>
      </CardHeader>
      <CardContent>
        <ul className="divide-y">
          {pressantes.map((t) => {
            const retard = trancheDe(t) === 'en_retard';
            return (
              <li key={t.id} className="flex min-h-14 items-start gap-3 py-3">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 shrink-0"
                  aria-label={`Marquer « ${t.titre} » comme fait`}
                  checked={false}
                  onChange={() => {
                    cocher.mutate(t.id);
                  }}
                />
                <div className="min-w-0 flex-1">
                  <div className="text-base font-semibold">{t.titre}</div>
                  <div className="text-muted-foreground flex flex-wrap items-center gap-x-3 text-sm">
                    <span className={retard ? 'text-prio-high font-medium' : ''}>
                      {retard ? 'En retard' : "Aujourd'hui"}
                    </span>
                    {t.priorite !== 'normale' ? <span>{LIBELLE_PRIORITE[t.priorite]}</span> : null}
                    {t.assigneANom !== null ? <span>{t.assigneANom}</span> : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
        {total > pressantes.length ? (
          <p className="text-muted-foreground pt-3 text-sm">
            et {total - pressantes.length} autre{total - pressantes.length > 1 ? 's' : ''}.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
